import React, { useMemo, useState, useEffect } from "react";
import { Modal, Row, Col } from "react-bootstrap";import { CiCirclePlus } from "react-icons/ci";
import { useDispatch, useSelector } from "react-redux";
import "react-phone-number-input/style.css";
import PhoneInput, { isPossiblePhoneNumber } from "react-phone-number-input";
import {
  createUser,
  fetchManagers,
  fetchAbsenceApprovers,
  uploadProfilePicture,
} from "../../../../redux/slices/userSlice";
import { fetchRoles } from "../../../../redux/slices/rolesSlice";
import { fetchDelegations } from "../../../../redux/slices/delegationSlice";
import useEmployeeValidation from "../../../reusableComps/EmployeeValidation/EmployeeValidation";
import { getNames } from "country-list";
import { useTranslation } from "react-i18next";
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from "../../../../utils/language";
import { ID_DOCUMENT_TYPE } from "../../../../utils/constants/user";

const countryOptions = getNames();

const NATIONALITIES = [
  "Alemã", "Americana", "Angolana", "Brasileira", "Britânica",
  "Cabo-verdiana", "Espanhola", "Francesa", "Guineense", "Italiana",
  "Moçambicana", "Portuguesa", "Santomense", "Suíça", "Timorense", "Outra",
];

const getErrorMessage = (error, fallback) => {
  const candidates = [
    error?.payload,
    error?.payload?.data,
    error?.data,
    error,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate;
    }

    const detail = candidate?.detail;
    if (typeof detail === "string" && detail.trim()) {
      return detail;
    }

    if (Array.isArray(detail)) {
      const validationMessage = detail
        .map((item) => {
          if (typeof item === "string") return item;
          const field = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : null;
          const message = item?.msg || item?.message;
          if (!message) return null;
          return field ? `${field}: ${message}` : message;
        })
        .filter(Boolean)
        .join(" | ");

      if (validationMessage) return validationMessage;
    }

    const message = candidate?.message;
    if (typeof message === "string" && message.trim() && message !== "Rejected") {
      return message;
    }
  }

  return fallback;
};

const getFieldErrors = (error) => {
  const detailCandidates = [
    error?.payload?.detail,
    error?.payload?.data?.detail,
    error?.detail,
    error?.data?.detail,
  ];
  const detail = detailCandidates.find(Array.isArray);
  const fieldErrors = {};

  if (detail) {
    detail.forEach((item) => {
      const field = Array.isArray(item?.loc)
        ? item.loc[item.loc.length - 1]
        : null;
      if (field && !["body", "query", "path"].includes(field) && item?.msg) {
        fieldErrors[field] = item.msg;
      }
    });
  }

  const message = getErrorMessage(error, "");
  if (message && !Object.keys(fieldErrors).length) {
    if (/email.*(already|registered|taken|duplicate)/i.test(message)) {
      fieldErrors.email = message;
    } else if (/username.*(already|registered|taken|duplicate)/i.test(message)) {
      fieldErrors.username = message;
    }
  }

  return fieldErrors;
};

const initialFormData = {
  first_name: "",
  last_name: "",
  username: "",
  role_id: "",
  delegation_id: null,
  phone_number: "",
  email: "",
  date_of_birth: "",
  hiring_date: "",
  manager_id: null,
  address: "",
  home_address: "",
  remote: false,
  profile_picture: "",
  country: "Portugal",
  region: "",
  city: "",
  locality: "",
  postal_code: "",
  position: "",
  team_tag: "",
  nationality: "",
  language: DEFAULT_LANGUAGE,
  emergency_contact_name: "",
  emergency_contact_phone: "",
  social_security_number: "",
  nif: "",
  iban: "",
  swift_bic: "",
  cc: "",
  employment_type: "Full Time",
  is_active: true,
  contract_type: "",
  contract_end_date: "",
  id_document_type: "",
  id_document_expiration_date: "",
  salary: "",
  absence_approver_id: null,
};

const CreateEmployeeModal = ({ show, onClose, onUserCreated }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const roles = useSelector((state) => state.roles?.items || []);
  const delegations = useSelector((state) => state.delegations?.items || []);
  const existingUsers = useSelector((state) => state.users?.items || []);
  const [managers, setManagers] = useState([]);
  const [absenceApprovers, setAbsenceApprovers] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [phoneRaw, setPhoneRaw] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [emergencyPhoneRaw, setEmergencyPhoneRaw] = useState("");
  const [emergencyPhoneTouched, setEmergencyPhoneTouched] = useState(false);

  const {
    form,
    setForm,
    errors,
    setErrors,
    touched,
    setTouched,
    handleInputChange,
    isValid,
  } = useEmployeeValidation(initialFormData);

  const employmentTypes = [
    { value: "Full Time", label: t("components.CreateEmployeeModal.fullTime", { defaultValue: "Full-time" }) },
    { value: "Part Time", label: t("components.CreateEmployeeModal.partTime", { defaultValue: "Part-time" }) },
    { value: "Contract", label: t("components.CreateEmployeeModal.contract", { defaultValue: "Contract" }) },
    { value: "Intern", label: t("components.CreateEmployeeModal.intern", { defaultValue: "Intern" }) },
    { value: "External", label: t("components.CreateEmployeeModal.external", { defaultValue: "External" }) },
  ];

  const contractTypes = [
    { value: "Open Ended", label: t("components.CreateEmployeeModal.contractTypes.openEnded", { defaultValue: "Sem Termo" }) },
    { value: "Fixed Term", label: t("components.CreateEmployeeModal.contractTypes.fixedTerm", { defaultValue: "Termo Certo" }) },
    { value: "Internship", label: t("components.CreateEmployeeModal.contractTypes.internship", { defaultValue: "Estágio" }) },
  ];

  const idDocumentTypes = [
    { value: ID_DOCUMENT_TYPE.CITIZEN_CARD, label: t("components.CreateEmployeeModal.idDocumentTypes.citizenCard", { defaultValue: "Cartão de Cidadão" }) },
    { value: ID_DOCUMENT_TYPE.PASSPORT, label: t("components.CreateEmployeeModal.idDocumentTypes.passport", { defaultValue: "Passaporte" }) },
    { value: ID_DOCUMENT_TYPE.RESIDENCE_CARD, label: t("components.CreateEmployeeModal.idDocumentTypes.residenceCard", { defaultValue: "Cartão de Residência" }) },
  ];

  useEffect(() => {
    if (!show) return;
    dispatch(fetchRoles());
    dispatch(fetchDelegations());

    dispatch(fetchManagers())
      .unwrap()
      .then((data) => setManagers(Array.isArray(data) ? data : []))
      .catch(() => setManagers([]));

    dispatch(fetchAbsenceApprovers())
      .unwrap()
      .then((data) => setAbsenceApprovers(Array.isArray(data) ? data : []))
      .catch(() => setAbsenceApprovers([]));
  }, [dispatch, show]);

  useEffect(() => {
    if (show) {
      setNotification(null);
    }
  }, [show]);

  useEffect(() => {
    if (!notification) return undefined;

    const timeoutId = window.setTimeout(
      () => setNotification(null),
      notification.type === "error" ? 10000 : 6000,
    );

    return () => window.clearTimeout(timeoutId);
  }, [notification]);

  const roleOptions = useMemo(
    () => roles.map((role) => ({ value: role.id, label: role.name })),
    [roles],
  );

  useEffect(() => {
    if (form.role_id || roleOptions.length === 0) return;
    const workerRole = roles.find((role) => role.name === "Worker");
    setForm((prev) => ({
      ...prev,
      role_id: workerRole?.id || roleOptions[0].value,
    }));
  }, [form.role_id, roleOptions, roles, setForm]);
  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(URL.createObjectURL(file));
    setForm((prev) => ({ ...prev, profile_picture_file: file }));
  };

  const handlePhoneChange = (value, fieldName) => {
    setForm((prev) => ({ ...prev, [fieldName]: value || "" }));
  };

  const isPhoneEmpty = !phoneRaw.trim();
  const isPhoneValid =
    isPhoneEmpty || (!!form.phone_number && isPossiblePhoneNumber(form.phone_number));

  const isEmergencyPhoneEmpty = !emergencyPhoneRaw.trim();
  const isEmergencyPhoneValid =
    isEmergencyPhoneEmpty ||
    (!!form.emergency_contact_phone && isPossiblePhoneNumber(form.emergency_contact_phone));

  const duplicateUser = useMemo(() => {
    const email = form.email?.trim().toLowerCase();
    const username = form.username?.trim().toLowerCase();

    return existingUsers.find((user) => {
      const sameEmail = email && user.email?.trim().toLowerCase() === email;
      const sameUsername = username && user.username?.trim().toLowerCase() === username;
      return sameEmail || sameUsername;
    });
  }, [existingUsers, form.email, form.username]);

  useEffect(() => {
    if (!duplicateUser) return;

    const email = form.email?.trim().toLowerCase();
    const duplicateField =
      email && duplicateUser.email?.trim().toLowerCase() === email
        ? "email"
        : "username";

    setErrors((previous) => ({
      ...previous,
      [duplicateField]: t(
        "components.CreateEmployeeModal.duplicate",
        duplicateField === "email"
          ? "Este email já está registado."
          : "Este username já está em uso.",
      ),
    }));
    setTouched((previous) => ({ ...previous, [duplicateField]: true }));
  }, [duplicateUser, form.email, setErrors, setTouched, t]);

  const canSubmit = Boolean(
    isValid &&
      !duplicateUser &&
      form.first_name?.trim() &&
      form.last_name?.trim() &&
      form.username?.trim() &&
      form.email?.trim() &&
      isPhoneValid &&
      isEmergencyPhoneValid,
  );

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    if (!canSubmit || isSubmitting) return;

    setIsSubmitting(true);
    setNotification(null);
    let photoUploadWarning = false;

    try {
      const payload = {
        ...form,
        date_of_birth: form.date_of_birth || null,
        hiring_date: form.hiring_date || null,
        salary: form.salary ? parseFloat(form.salary) : null,
        contract_end_date: form.contract_end_date || null,
        id_document_expiration_date: form.id_document_expiration_date || null,
        manager_id: form.manager_id || null,
        delegation_id: form.delegation_id || null,
        absence_approver_id: form.absence_approver_id || null,
        contract_type: form.contract_type || null,
        id_document_type: form.id_document_type || null,
        employment_type: form.employment_type || null,
        country: form.country || null,
        password: form.password?.trim() || "password123",
      };

      delete payload.profile_picture_file;

      const response = await dispatch(createUser(payload)).unwrap();
      const createdUser = response?.user || response?.data?.user || response?.data || response;
      const newUserId = createdUser?.id;

      if (newUserId && form.profile_picture_file) {
        try {
          const uploadResult = await dispatch(
            uploadProfilePicture({
              file: form.profile_picture_file,
              userId: newUserId,
            }),
          ).unwrap();

          const profilePath = uploadResult?.path || uploadResult?.profile_picture;
          if (!profilePath) {
            throw new Error("The uploaded profile picture did not include a path.");          }
        } catch (uploadError) {
          photoUploadWarning = true;
          console.error("Error uploading profile picture:", uploadError);
        }
      } else if (form.profile_picture_file) {
        photoUploadWarning = true;
      }

      setForm(initialFormData);
      setImagePreview(null);
      setPhoneRaw("");
      setPhoneTouched(false);
      setEmergencyPhoneRaw("");
      setEmergencyPhoneTouched(false);

      if (onUserCreated) {
        try {
          onUserCreated(createdUser);
        } catch (callbackError) {
          console.error("Error refreshing the employee list:", callbackError);
        }
      }

      setNotification({
        type: photoUploadWarning ? "warning" : "success",
        message: photoUploadWarning
          ? t("components.CreateEmployeeModal.createdWithPhotoWarning", {
              defaultValue:
                "Colaborador criado, mas a fotografia não foi carregada. Tente novamente mais tarde.",
            })
          : t("components.CreateEmployeeModal.created", {
              defaultValue: "Colaborador criado com sucesso.",
            }),
      });

      onClose();
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        t("components.CreateEmployeeModal.error", {
          defaultValue: "Não foi possível criar o colaborador.",
        }),
      );

      const fieldErrors = getFieldErrors(error);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors((previous) => ({ ...previous, ...fieldErrors }));
        setTouched((previous) => ({
          ...previous,
          ...Object.fromEntries(Object.keys(fieldErrors).map((field) => [field, true])),
        }));
      }

      console.error("Error creating user:", errorMessage);
      setNotification({ type: "error", message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getUserDisplayName = (user) => {
    const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
    return fullName || user.username || `Utilizador #${user.id}`;
  };

  const ccLabel =
    form.id_document_type === ID_DOCUMENT_TYPE.PASSPORT
      ? t("components.CreateEmployeeModal.passportNumber", { defaultValue: "Número do Passaporte" })
      : form.id_document_type === ID_DOCUMENT_TYPE.RESIDENCE_CARD
      ? t("components.CreateEmployeeModal.residenceCardNumber", { defaultValue: "Número do Cartão de Residência" })
      : t("components.CreateEmployeeModal.ccNumber", { defaultValue: "Número do Cartão de Cidadão" });

  const handleClose = () => {
    if (isSubmitting) return;
    setNotification(null);
    onClose();
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} size="xl" centered backdrop="static">
      <div className="modal-content bg-surface border-0">
        <form onSubmit={handleCreateEmployee} className="w-100" noValidate>
          <div className="modal-header d-flex justify-content-between align-items-start border-0 px-4 pt-4 pb-0">
            <div className="d-flex align-items-center gap-2">
              <div
                style={{
                  width: "4px",
                  height: "24px",
                  backgroundColor: "var(--brand-primary)",
                  borderRadius: "2px",
                }}
              ></div>
              <h1 className="modal-title fs-5 mb-0 txt-main fw-bold">
                {t("components.CreateEmployeeModal.title", { defaultValue: "Novo Colaborador" })}
              </h1>
            </div>

            <div className="d-flex align-items-end flex-column gap-1">
              <div className="d-flex align-items-center gap-3">
                <button
                  type="submit"
                  className="btn text-white fw-bold d-flex align-items-center justify-content-center gap-2"
                  style={{
                    backgroundColor: "var(--brand-primary)",
                    border: "none",
                    minWidth: "max-content",
                    padding: "0.5rem 1.25rem",
                  }}
                  disabled={!canSubmit || isSubmitting}
                >
                  <CiCirclePlus className="fs-4" />
                  <span>
                    {isSubmitting
                      ? t("common.saving", { defaultValue: "A guardar..." })
                      : t("common.add", { defaultValue: "Adicionar" })}
                  </span>
                </button>

                <button
                  type="button"
                  className="btn-close m-0"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  aria-label="Close"
                ></button>
              </div>


            </div>
          </div>

          {notification && (
            <div
              className={`alert alert-${notification.type} mx-4 mt-3 mb-0 d-flex align-items-start gap-2`}
              role={notification.type === "error" ? "alert" : "status"}
              aria-live={notification.type === "error" ? "assertive" : "polite"}
            >
              <span className="flex-grow-1">{notification.message}</span>
              <button
                type="button"
                className="btn-close"
                onClick={() => setNotification(null)}
                aria-label={t("common.close", { defaultValue: "Fechar" })}
              ></button>
            </div>
          )}

          <div
            className="modal-body px-4 pt-3 pb-4 custom-scrollbar"
            style={{ maxHeight: "75vh", overflowY: "auto" }}
          >
            {!canSubmit && !isSubmitting && (Object.keys(touched).length > 0 || duplicateUser) && (
              <div className="alert alert-light border d-flex align-items-center gap-2 mb-3" role="status">
                <span>
                  {t("components.CreateEmployeeModal.completeRequired", "Completa os campos obrigatórios para continuar.")}
                </span>
              </div>
            )}

            <div className="atlar-card p-3 mb-4">
              <Row>
                <Col
                  md={3}
                  className="d-flex justify-content-center align-items-center mb-3 mb-md-0"
                >
                  <div className="w-100 text-center">
                    <label
                      htmlFor="profile-upload"
                      className="btn btn-outline-secondary btn-sm mb-3 rounded-pill"
                      style={{ cursor: "pointer" }}
                    >
                      {t("components.CreateEmployeeModal.uploadPhoto", { defaultValue: "Upload Foto" })}
                    </label>
                    <input
                      id="profile-upload"
                      type="file"
                      className="d-none"
                      name="profile_picture"
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                    <div
                      className="mx-auto rounded-circle bg-light d-flex align-items-center justify-content-center overflow-hidden border"
                      style={{ width: "190px", height: "190px" }}
                    >
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt={form.username}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <span className="txt-subtle">
                          {t("components.CreateEmployeeModal.noPhoto", { defaultValue: "Sem Foto" })}
                        </span>
                      )}
                    </div>
                  </div>
                </Col>

                <Col md={9}>
                  <Row className="h-100 align-items-center g-3">
                    <Col xs={6}>
                      <label className="form-label txt-subtle fw-bold mb-1" style={{ fontSize: "0.875rem" }}>
                        {t("components.CreateEmployeeModal.firstName", { defaultValue: "Primeiro Nome" })}
                      </label>
                      <input
                        type="text"
                        required
                        aria-required="true"
                        className={`form-control ${touched.first_name && errors.first_name ? "is-invalid" : ""}`}
                        name="first_name"
                        value={form.first_name}
                        onChange={handleInputChange}
                      />
                      {touched.first_name && errors.first_name && (
                        <div className="invalid-feedback d-block">{errors.first_name}</div>
                      )}
                    </Col>

                    <Col xs={6}>
                      <label className="form-label txt-subtle fw-bold mb-1" style={{ fontSize: "0.875rem" }}>
                        {t("components.CreateEmployeeModal.lastName", { defaultValue: "Último Nome" })}
                      </label>
                      <input
                        type="text"
                        required
                        aria-required="true"
                        className={`form-control ${touched.last_name && errors.last_name ? "is-invalid" : ""}`}
                        name="last_name"
                        value={form.last_name}
                        onChange={handleInputChange}
                      />
                      {touched.last_name && errors.last_name && (
                        <div className="invalid-feedback d-block">{errors.last_name}</div>
                      )}
                    </Col>

                    <Col xs={12}>
                      <label className="form-label txt-subtle fw-bold mb-1" style={{ fontSize: "0.875rem" }}>
                        {t("components.CreateEmployeeModal.username", { defaultValue: "Nome de utilizador" })}
                      </label>
                      <input
                        type="text"
                        required
                        aria-required="true"
                        className={`form-control ${touched.username && errors.username ? "is-invalid" : ""}`}
                        name="username"
                        value={form.username}
                        onChange={handleInputChange}
                      />
                      {touched.username && errors.username && (
                        <div className="invalid-feedback d-block">{errors.username}</div>
                      )}
                    </Col>

                    <Col xs={4}>
                      <label className="form-label txt-subtle fw-bold mb-1" style={{ fontSize: "0.875rem" }}>
                        {t("components.CreateEmployeeModal.role", { defaultValue: "Cargo" })}
                      </label>
                      <select
                        className={`form-select ${touched.role_id && errors.role_id ? "is-invalid" : ""}`}
                        name="role_id"
                        value={form.role_id}
                        onChange={handleInputChange}
                      >
                        {roleOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      {touched.role_id && errors.role_id && (
                        <div className="invalid-feedback d-block">{errors.role_id}</div>
                      )}
                    </Col>

                    <Col xs={4}>
                      <label className="form-label txt-subtle fw-bold mb-1" style={{ fontSize: "0.875rem" }}>
                        {t("components.CreateEmployeeModal.phone", { defaultValue: "Nº de telemóvel" })}
                      </label>
                      <PhoneInput
                        international
                        defaultCountry="PT"
                        value={form.phone_number || ""}
                        onChange={(val) => handlePhoneChange(val, "phone_number")}
                        className={`form-control bg-surface txt-main ${phoneTouched && !isPhoneValid ? "is-invalid" : ""}`}
                        numberInputProps={{
                          className: "border-0",
                          onChange: (e) => {
                            setPhoneRaw(e.target.value);
                            setPhoneTouched(true);
                          },
                          onBlur: () => setPhoneTouched(true),
                        }}
                      />
                      {phoneTouched && !isPhoneValid && (
                        <div className="invalid-feedback d-block">
                          {t("components.CreateEmployeeModal.invalidPhone", { defaultValue: "Número de telemóvel inválido ou incompleto" })}
                        </div>
                      )}
                    </Col>

                    <Col xs={4}>
                      <label className="form-label txt-subtle fw-bold mb-1" style={{ fontSize: "0.875rem" }}>
                        {t("components.CreateEmployeeModal.email", { defaultValue: "Email" })}
                      </label>
                      <input
                        type="email"
                        required
                        aria-required="true"
                        className={`form-control ${touched.email && errors.email ? "is-invalid" : ""}`}
                        name="email"
                        value={form.email}
                        onChange={handleInputChange}
                      />
                      {touched.email && errors.email && (
                        <div className="invalid-feedback d-block">{errors.email}</div>
                      )}
                    </Col>

                    <Col xs={6}>
                      <label className="form-label txt-subtle fw-bold mb-1" style={{ fontSize: "0.875rem" }}>
                        {t("components.CreateEmployeeModal.dob", { defaultValue: "Data de nascimento" })}
                      </label>
                      <input
                        type="date"
                        className={`form-control ${touched.date_of_birth && errors.date_of_birth ? "is-invalid" : ""}`}
                        name="date_of_birth"
                        value={form.date_of_birth ? form.date_of_birth.slice(0, 10) : ""}
                        onChange={handleInputChange}
                      />
                      {touched.date_of_birth && errors.date_of_birth && (
                        <div className="invalid-feedback d-block">{errors.date_of_birth}</div>
                      )}
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>

            <div className="atlar-card p-3 mb-4">
              <h6 className="fw-bold txt-main mb-3">
                {t("components.CreateEmployeeModal.contractInfo", { defaultValue: "Informações de Contrato" })}
              </h6>
              <Row className="g-3">
                <Col md={4}>
                  <label className="form-label mb-1 txt-subtle fw-bold" style={{ fontSize: "0.875rem" }}>
                    {t("components.CreateEmployeeModal.contractTypeLabel", { defaultValue: "Tipo de Contrato" })}
                  </label>
                  <select
                    className={`form-select ${touched.contract_type && errors.contract_type ? "is-invalid" : ""}`}
                    name="contract_type"
                    value={form.contract_type}
                    onChange={handleInputChange}
                  >
                    <option value="">{t("common.select", { defaultValue: "Selecione..." })}</option>
                    {contractTypes.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {touched.contract_type && errors.contract_type && (
                    <div className="invalid-feedback d-block">{errors.contract_type}</div>
                  )}
                </Col>

                {(form.contract_type === "Fixed Term" || form.contract_type === "Internship") && (
                  <Col md={4}>
                    <label className="form-label mb-1 txt-subtle fw-bold" style={{ fontSize: "0.875rem" }}>
                      {t("components.CreateEmployeeModal.contractEndDate", { defaultValue: "Data de Fim de Contrato" })}
                    </label>
                    <input
                      type="date"
                      className={`form-control ${touched.contract_end_date && errors.contract_end_date ? "is-invalid" : ""}`}
                      name="contract_end_date"
                      value={form.contract_end_date ? form.contract_end_date.slice(0, 10) : ""}
                      onChange={handleInputChange}
                    />
                    {touched.contract_end_date && errors.contract_end_date && (
                      <div className="invalid-feedback d-block">{errors.contract_end_date}</div>
                    )}
                  </Col>
                )}

                <Col md={4}>
                  <label className="form-label mb-1 txt-subtle fw-bold" style={{ fontSize: "0.875rem" }}>
                    {t("components.CreateEmployeeModal.salaryLabel", { defaultValue: "Salário (€)" })}
                  </label>
                  <input
                    type="text"
                    className={`form-control ${touched.salary && errors.salary ? "is-invalid" : ""}`}
                    name="salary"
                    value={form.salary}
                    onChange={handleInputChange}
                    placeholder="ex: 1500.00"
                  />
                  {touched.salary && errors.salary && (
                    <div className="invalid-feedback d-block">{errors.salary}</div>
                  )}
                </Col>

                <Col md={4}>
                  <label className="form-label mb-1 txt-subtle fw-bold" style={{ fontSize: "0.875rem" }}>
                    {t("components.CreateEmployeeModal.idDocumentTypeLabel", { defaultValue: "Tipo de Documento de ID" })}
                  </label>
                  <select
                    className={`form-select ${touched.id_document_type && errors.id_document_type ? "is-invalid" : ""}`}
                    name="id_document_type"
                    value={form.id_document_type}
                    onChange={handleInputChange}
                  >
                    <option value="">{t("common.select", { defaultValue: "Selecione..." })}</option>
                    {idDocumentTypes.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </Col>

                <Col md={4}>
                  <label className="form-label mb-1 txt-subtle fw-bold" style={{ fontSize: "0.875rem" }}>
                    {ccLabel}
                  </label>
                  <input
                    type="text"
                    className={`form-control ${touched.cc && errors.cc ? "is-invalid" : ""}`}
                    name="cc"
                    value={form.cc || ""}
                    onChange={handleInputChange}
                    placeholder={
                      form.id_document_type === ID_DOCUMENT_TYPE.CITIZEN_CARD
                        ? "ex: 123456789ZZ1"
                        : "ex: AB123456"
                    }
                  />
                  {touched.cc && errors.cc && (
                    <div className="invalid-feedback d-block">{errors.cc}</div>
                  )}
                </Col>

                <Col md={4}>
                  <label className="form-label mb-1 txt-subtle fw-bold" style={{ fontSize: "0.875rem" }}>
                    {t("components.CreateEmployeeModal.idDocumentExpirationLabel", { defaultValue: "Data de Expiração do ID" })}
                  </label>
                  <input
                    type="date"
                    className={`form-control ${touched.id_document_expiration_date && errors.id_document_expiration_date ? "is-invalid" : ""}`}
                    name="id_document_expiration_date"
                    value={form.id_document_expiration_date ? form.id_document_expiration_date.slice(0, 10) : ""}
                    onChange={handleInputChange}
                  />
                  {touched.id_document_expiration_date && errors.id_document_expiration_date && (
                    <div className="invalid-feedback d-block">{errors.id_document_expiration_date}</div>
                  )}
                </Col>

                <Col md={4}>
                  <label className="form-label mb-1 txt-subtle fw-bold" style={{ fontSize: "0.875rem" }}>
                    {t("components.CreateEmployeeModal.absenceApproverLabel", { defaultValue: "Aprovador de Ausências" })}
                  </label>
                  <select
                    className="form-select"
                    name="absence_approver_id"
                    value={form.absence_approver_id || ""}
                    onChange={handleInputChange}
                  >
                    <option value="">{t("components.CreateEmployeeModal.noApprover", { defaultValue: "Sem aprovador" })}</option>
                    {absenceApprovers.map((approver) => (
                      <option key={approver.id} value={approver.id}>{getUserDisplayName(approver)}</option>
                    ))}
                  </select>
                </Col>
              </Row>
            </div>

            <div className="atlar-card p-3 mb-4">
              <h6 className="fw-bold txt-main mb-3">
                {t("components.CreateEmployeeModal.professionalInfo", { defaultValue: "Informação Profissional" })}
              </h6>
              <Row className="g-3">
                <Col md={4}>
                  <label className="form-label mb-1 txt-subtle fw-bold" style={{ fontSize: "0.875rem" }}>
                    {t("components.CreateEmployeeModal.manager", { defaultValue: "Gestor (Reports To)" })}
                  </label>
                  <select
                    className={`form-select ${errors.manager_id ? "is-invalid" : ""}`}
                    name="manager_id"
                    value={form.manager_id || ""}
                    onChange={handleInputChange}
                  >
                    <option value="">{t("components.CreateEmployeeModal.noManager", { defaultValue: "Sem gestor" })}</option>
                    {managers.map((manager) => (
                      <option key={manager.id} value={manager.id}>{getUserDisplayName(manager)}</option>
                    ))}
                  </select>
                  {errors.manager_id && (
                    <div className="invalid-feedback d-block">{errors.manager_id}</div>
                  )}
                </Col>
                <Col md={4}>
                  <label className="form-label mb-1 txt-subtle fw-bold" style={{ fontSize: "0.875rem" }}>
                    {t("components.CreateEmployeeModal.delegation", { defaultValue: "Delegação / Local" })}
                  </label>
                  <select
                    className={`form-select ${errors.delegation_id ? "is-invalid" : ""}`}
                    name="delegation_id"
                    value={form.delegation_id || ""}
                    onChange={handleInputChange}
                  >
                    <option value="">{t("components.CreateEmployeeModal.selectDelegation", { defaultValue: "Selecione Delegação" })}</option>
                    {delegations.map((delegation) => (
                      <option key={delegation.id} value={delegation.id}>{delegation.name}</option>
                    ))}
                  </select>
                  {errors.delegation_id && (
                    <div className="invalid-feedback d-block">{errors.delegation_id}</div>
                  )}
                </Col>
                <Col md={4}>
                  <label className="form-label mb-1 txt-subtle fw-bold" style={{ fontSize: "0.875rem" }}>
                    {t("components.CreateEmployeeModal.hiringDate", { defaultValue: "Data de Contratação" })}
                  </label>
                  <input
                    type="date"
                    className={`form-control ${touched.hiring_date && errors.hiring_date ? "is-invalid" : ""}`}
                    name="hiring_date"
                    value={form.hiring_date ? form.hiring_date.slice(0, 10) : ""}
                    onChange={handleInputChange}
                  />
                  {touched.hiring_date && errors.hiring_date && (
                    <div className="invalid-feedback d-block">{errors.hiring_date}</div>
                  )}
                </Col>
              </Row>
            </div>

            <div className="atlar-card p-3">
              <h6 className="fw-bold txt-main mb-3">
                {t("components.CreateEmployeeModal.documentation", { defaultValue: "Documentação e Detalhes" })}
              </h6>
              <div className="row g-3">
                {[
                  { label: t("components.CreateEmployeeModal.nif", { defaultValue: "NIF" }), name: "nif" },
                  { label: t("components.CreateEmployeeModal.iban", { defaultValue: "IBAN" }), name: "iban" },
                  { label: t("components.CreateEmployeeModal.swiftBic", { defaultValue: "SWIFT/BIC" }), name: "swift_bic" },
                  { label: t("components.CreateEmployeeModal.ssn", { defaultValue: "Nº Seg. Social" }), name: "social_security_number" },
                  { label: t("components.CreateEmployeeModal.position", { defaultValue: "Posição / Título" }), name: "position" },
                  { label: t("components.CreateEmployeeModal.teamTag", { defaultValue: "Equipa" }), name: "team_tag" },
                  { label: t("components.CreateEmployeeModal.employmentType", { defaultValue: "Tipo de Emprego" }), name: "employment_type" },
                  { label: t("components.CreateEmployeeModal.nationality", { defaultValue: "Nacionalidade" }), name: "nationality" },
                  { label: t("components.CreateEmployeeModal.language", { defaultValue: "Idioma" }), name: "language" },
                  { label: t("components.CreateEmployeeModal.address", { defaultValue: "Endereço" }), name: "address" },
                  { label: t("components.CreateEmployeeModal.homeAddress", { defaultValue: "Endereço de Casa" }), name: "home_address" },
                  { label: t("components.CreateEmployeeModal.country", { defaultValue: "País" }), name: "country" },
                  { label: t("components.CreateEmployeeModal.region", { defaultValue: "Região" }), name: "region" },
                  { label: t("components.CreateEmployeeModal.city", { defaultValue: "Cidade" }), name: "city" },
                  { label: t("components.CreateEmployeeModal.postalCode", { defaultValue: "Código Postal" }), name: "postal_code" },
                  { label: t("components.CreateEmployeeModal.locality", { defaultValue: "Localidade" }), name: "locality" },
                  { label: t("components.CreateEmployeeModal.emergencyContactName", { defaultValue: "Nome do Contacto de Emergência" }), name: "emergency_contact_name" },
                ].map((item, index) => (
                  <div className="col-md-4" key={index}>
                    <label className="form-label mb-1 txt-subtle fw-bold" style={{ fontSize: "0.875rem" }}>
                      {item.label}
                    </label>
                    {item.name === "country" ? (
                      <select className="form-select" name="country" value={form.country} onChange={handleInputChange}>
                        {countryOptions.map((country) => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                    ) : item.name === "employment_type" ? (
                      <select
                        className={`form-select ${touched[item.name] && errors[item.name] ? "is-invalid" : ""}`}
                        name="employment_type"
                        value={form.employment_type}
                        onChange={handleInputChange}
                      >
                        {employmentTypes.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    ) : item.name === "nationality" ? (
                      <select
                        className={`form-select ${touched[item.name] && errors[item.name] ? "is-invalid" : ""}`}
                        name="nationality"
                        value={form.nationality}
                        onChange={handleInputChange}
                      >
                        <option value="" disabled>
                          {t("common.select", { defaultValue: "Selecione..." })}
                        </option>
                        {NATIONALITIES.map((nat) => (
                          <option key={nat} value={nat}>{nat}</option>
                        ))}
                      </select>
                    ) : item.name === "language" ? (
                      <select
                        className={`form-select ${touched[item.name] && errors[item.name] ? "is-invalid" : ""}`}
                        name="language"
                        value={form.language}
                        onChange={handleInputChange}
                      >
                        {SUPPORTED_LANGUAGES.map((option) => (
                          <option key={option.code} value={option.code}>
                            {t(option.labelKey, { defaultValue: option.fallbackLabel })}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <>
                        <input
                          type="text"
                          className={`form-control ${touched[item.name] && errors[item.name] ? "is-invalid" : ""}`}
                          name={item.name}
                          value={form[item.name] || ""}
                          onChange={handleInputChange}
                        />
                        {touched[item.name] && errors[item.name] && (
                          <div className="invalid-feedback d-block">{errors[item.name]}</div>
                        )}
                      </>
                    )}
                  </div>
                ))}

                <div className="col-md-4">
                  <label className="form-label mb-1 txt-subtle fw-bold" style={{ fontSize: "0.875rem" }}>
                    {t("components.CreateEmployeeModal.emergencyContactPhone", { defaultValue: "Telefone de Emergência" })}
                  </label>
                  <PhoneInput
                    international
                    defaultCountry="PT"
                    value={form.emergency_contact_phone || ""}
                    onChange={(val) => handlePhoneChange(val, "emergency_contact_phone")}
                    className={`form-control bg-surface txt-main ${emergencyPhoneTouched && !isEmergencyPhoneValid ? "is-invalid" : ""}`}
                    numberInputProps={{
                      className: "border-0",
                      onChange: (e) => {
                        setEmergencyPhoneRaw(e.target.value);
                        setEmergencyPhoneTouched(true);
                      },
                      onBlur: () => setEmergencyPhoneTouched(true),
                    }}
                  />
                  {emergencyPhoneTouched && !isEmergencyPhoneValid && (
                    <div className="invalid-feedback d-block">
                      {t("components.CreateEmployeeModal.invalidPhone", { defaultValue: "Número de telemóvel inválido ou incompleto" })}
                    </div>
                  )}
                </div>

                <div className="col-12 mt-4 d-flex gap-4">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="remoteSwitch"
                      name="remote"
                      checked={!!form.remote}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label fw-bold txt-main" htmlFor="remoteSwitch">
                      {t("components.CreateEmployeeModal.remote", { defaultValue: "Remoto" })}
                    </label>
                  </div>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="activeSwitch"
                      name="is_active"
                      checked={!!form.is_active}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label fw-bold txt-main" htmlFor="activeSwitch">
                      {t("components.CreateEmployeeModal.active", { defaultValue: "Conta Ativa" })}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </Modal>

      {notification && !show && (
        <div
          className="position-fixed top-0 end-0 p-3"
          style={{ zIndex: 1090 }}
          aria-live={notification.type === "error" ? "assertive" : "polite"}
        >
          <div
            className={`alert alert-${notification.type} shadow mb-0 d-flex align-items-start gap-2`}
            role={notification.type === "error" ? "alert" : "status"}
          >
            <span className="flex-grow-1">{notification.message}</span>
            <button
              type="button"
              className="btn-close"
              onClick={() => setNotification(null)}
              aria-label={t("common.close", { defaultValue: "Fechar" })}
            ></button>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateEmployeeModal;